class CartDrawerSection extends HTMLElement {
  constructor() {
    super();

    this.cartType = this.dataset.cartType || "drawer";
    this.drawerClass = "wt-cart__drawer";
    this.drawer = this.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--open`;
    this.pageOverlayClass = "page-overlay-cart";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;
    this.body = document.body;

    this.triggerQuery = [
      ".wt-cart__trigger",
      ".wt-cart__back-link",
      `.${this.pageOverlayClass}`,
    ].join(", ");
    this.triggers = () => document.querySelectorAll(this.triggerQuery);
    this.isOpen = false;

    const cartUrl =
      (window.routes && window.routes.cart_url) ||
      (window.Shopify && window.Shopify.routes && window.Shopify.routes.cart) ||
      "/cart";

    this.isCartPage = window.location.pathname === cartUrl;

    this.closeButton = () => this.querySelector(".wt-cart__drawer__close");
    this.mainTrigger = document.querySelector(".wt-cart__trigger");

    this.toggleElements = () => {
      const selector = this.dataset.toggleTabindex;
      return selector ? this.querySelectorAll(selector) : [];
    };

    this.cartUpdateUnsubscriber = null;
  }

  connectedCallback() {
    if (!this.drawer) {
      // není co inicializovat, bezpečně skončíme
      return;
    }

    if (this.cartType === "page" || this.isCartPage) {
      document.addEventListener("cart-drawer:refresh", (e) =>
        this.refreshCartDrawer(e),
      );
      return;
    }

    this.init();

    if (
      typeof subscribe === "function" &&
      window.PUB_SUB_EVENTS &&
      PUB_SUB_EVENTS.cartUpdate
    ) {
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        () => {
          if (this.isOpen) {
            if (typeof setTabindex === "function") {
              setTabindex(this.toggleElements(), "0");
            }
            const closeBtn = this.closeButton();
            if (closeBtn) closeBtn.focus();
          }
        },
      );
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  getFocusableElements() {
    const focusableElementsSelector =
      "button, [href], input, select, [tabindex]";
    const focusableElements = () =>
      Array.from(this.querySelectorAll(focusableElementsSelector)).filter(
        (el) => !el.hasAttribute("disabled") && el.tabIndex >= 0,
      );

    const all = focusableElements();
    return {
      focusableElements,
      first: all[0],
      last: all[all.length - 1],
    };
  }

  temporaryHideFocusVisible() {
    document.body.classList.add("no-focus-visible");
    setTimeout(() => {
      document.body.classList.remove("no-focus-visible");
    }, 100);
  }

  onToggle() {
    const toggles = this.toggleElements();

    if (this.hasAttribute("open")) {
      this.removeAttribute("open");
      this.isOpen = false;
      if (this.mainTrigger) this.mainTrigger.focus();
      this.temporaryHideFocusVisible();
      if (typeof setTabindex === "function") {
        setTabindex(toggles, "-1");
      }
    } else {
      this.setAttribute("open", "");
      this.isOpen = true;
      const closeBtn = this.closeButton();
      if (closeBtn) closeBtn.focus();
      this.temporaryHideFocusVisible();
      if (typeof setTabindex === "function") {
        setTabindex(toggles, "0");
      }
    }
  }

  toggleDrawerClasses() {
    this.onToggle();
    if (this.drawer) {
      this.drawer.classList.toggle(this.classDrawerActive);
    }
    if (this.body) {
      this.body.classList.toggle(this.activeOverlayBodyClass);
    }
  }

  init() {
    this.addEventListener("keydown", (e) => {
      const isTabPressed =
        e.key === "Tab" || e.keyCode === 9 || e.code === "Tab";
      const { first, last } = this.getFocusableElements();

      if (
        e.key === "Escape" ||
        e.keyCode === 27 ||
        e.code === "Escape"
      ) {
        if (this.isOpen) {
          this.toggleDrawerClasses();
        }
      }

      if (!first || !last) return;

      if (isTabPressed) {
        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    });

    this.triggers().forEach((trigger) => {
      trigger.addEventListener("click", (e) => {
        e.preventDefault();
        this.toggleDrawerClasses();
      });
    });

    this.addEventListener("click", (e) => {
      if (e.target.classList.contains("wt-cart__drawer__close")) {
        e.preventDefault();
        this.toggleDrawerClasses();
      }
    });

    document.addEventListener("cart-drawer:refresh", (e) =>
      this.refreshCartDrawer(e),
    );
  }

  renderContents(parsedState, isClosedCart = true) {
    if (!parsedState || !parsedState.sections) return;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections[section.id]) return;

      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    if (isClosedCart) {
      setTimeout(() => {
        this.toggleDrawerClasses();
        if (this.isOpen) {
          const closeBtn = this.closeButton();
          if (closeBtn) closeBtn.focus();
        }
      });
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : "";
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
        selector: "#cart-icon-bubble",
      },
    ];
  }

  refreshCartDrawer() {
    const sectionsToRender = this.getSectionsToRender();
    const sectionNames = sectionsToRender.map((s) => s.id).join(",");

    const root =
      (window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root) ||
      "/";

    fetch(`${root}?sections=${sectionNames}`)
      .then((response) => response.json())
      .then((response) => {
        const parsedState = {
          sections: response,
        };
        this.renderContents(parsedState, false);
      })
      .catch((error) => {
        console.error("Cart drawer refresh failed", error);
      });
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawerSection);

if (typeof CartItems !== "undefined") {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: "CartDrawer",
          section: "cart-drawer",
          selector: ".drawer__inner",
        },
        {
          id: "cart-icon-bubble",
          section: "cart-icon-bubble",
          selector: ".shopify-section",
        },
      ];
    }
  }

  customElements.define("cart-drawer-items", CartDrawerItems);
}
